import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PAYMENT } from "@/constants/testIds";
import DiscordCTA from "@/components/DiscordCTA";

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;
const DISCORD_REDIRECT_SECONDS = 5;

export default function PaymentReturn() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState("pending");
  const [attempts, setAttempts] = useState(0);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(DISCORD_REDIRECT_SECONDS);
  const [skipDiscord, setSkipDiscord] = useState(false);

  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");
  const isFree = params.get("free") === "1";

  // Auto-redirect the new paid member into the Discord OAuth link flow.
  // Only fires if:
  //   1. Payment/enrollment succeeded (status === "paid"),
  //   2. The user hasn't already linked Discord (so returning members
  //      don't get bounced through OAuth every checkout),
  //   3. They didn't click "Skip for now".
  // This is the highest-intent moment to close the referral loop AND
  // pull the member into the community — one click gets them both.
  useEffect(() => {
    if (status !== "paid" || skipDiscord) return;
    if (user?.discord_linked || user?.discord_user_id) return;
    if (countdown <= 0) {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      window.location.href = `${API}/api/discord/oauth/start`;
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown, user, skipDiscord]);

  const poll = useCallback(async (currentAttempts) => {
    if (!sessionId) return;
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      setPayload(data);
      if (data.payment_status === "paid") {
        setStatus("paid");
        await refresh();
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      if (currentAttempts + 1 >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(() => setAttempts(currentAttempts + 1), INTERVAL_MS);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message || t("paymentReturnUi.unknownError"));
      setStatus("error");
    }
  }, [sessionId, refresh]);

  useEffect(() => {
    // Launch-promo `?free=1` path — backend has already flipped the user's
    // tier server-side (see routes_subscription._effective_amount == 0
    // branch). No Stripe session exists to poll, so just refresh the auth
    // context to pick up the new tier and show the success state.
    if (isFree) {
      (async () => {
        try { await refresh(); } catch { /* ignore */ }
        setPayload({ tier: "basic" });
        setStatus("paid");
      })();
      return;
    }
    if (!sessionId) {
      setError(t("paymentReturnUi.missingSessionId"));
      setStatus("error");
      return;
    }
    if (status === "pending") poll(attempts);
  }, [attempts, poll, status, sessionId, isFree, refresh]);

  return (
    <div data-testid={PAYMENT.root} className="max-w-2xl mx-auto px-6 py-24 text-center">
      {status === "pending" && (
        <>
          <Clock className="mx-auto text-blue-800" size={44} />
          <h1 data-testid={PAYMENT.statusText} className="mt-6 font-display font-bold text-3xl text-slate-900">
            {t("paymentReturnUi.confirmingPayment")}
          </h1>
          <p className="mt-3 text-slate-600">
            {t("paymentReturnUi.hangTight")}
          </p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-blue-600" size={48} />
          <h1 data-testid={PAYMENT.statusText} className="mt-6 font-display font-bold text-4xl text-slate-900 tracking-tight">
            {t("paymentReturnUi.youreIn")}
          </h1>
          <p className="mt-3 text-slate-600 max-w-md mx-auto">
            {t("paymentReturnUi.welcomeToTier", { tier: payload?.tier })} {t("paymentReturnUi.everythingUnlocked")}
          </p>

          {/* Discord auto-link countdown — only when the user hasn't already
              linked. Post-checkout is the highest-intent moment to close the
              community loop AND (for referred members) trigger their referrer's
              1/3 unlock signal. */}
          {!(user?.discord_linked || user?.discord_user_id) && !skipDiscord && (
            <div
              data-testid="payment-discord-auto-link"
              className="mt-8 mx-auto max-w-md rounded-2xl border border-indigo-200 bg-indigo-50/70 p-5"
            >
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-indigo-800">
                {t("paymentReturnUi.oneLastStep")}
              </div>
              <div className="mt-1 text-slate-900 font-semibold">
                {t("paymentReturnUi.linkingDiscordIn", { countdown })}
              </div>
              <div className="mt-1 text-sm text-slate-600">
                {t("paymentReturnUi.discordBenefits")}
              </div>
              <div className="mt-4 flex items-center justify-center gap-3">
                <Button
                  onClick={() => {
                    const API = process.env.REACT_APP_BACKEND_URL || "";
                    window.location.href = `${API}/api/discord/oauth/start`;
                  }}
                  data-testid="payment-discord-link-now"
                  className="bg-[#5865F2] hover:bg-[#4752C4] text-white h-10 px-5"
                >
                  {t("paymentReturnUi.linkNow")}
                </Button>
                <button
                  type="button"
                  onClick={() => setSkipDiscord(true)}
                  data-testid="payment-discord-skip"
                  className="text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
                >
                  {t("paymentReturnUi.skipForNow")}
                </button>
              </div>
            </div>
          )}

          <Button
            onClick={() => navigate("/dashboard")}
            className="mt-8 bg-blue-800 hover:bg-blue-700 text-white h-11 px-8"
          >
            {t("paymentReturnUi.goToDashboard")}
          </Button>
          {(user?.discord_linked || user?.discord_user_id || skipDiscord) && (
            <div className="mt-6 flex justify-center">
              <DiscordCTA label={t("paymentReturnUi.joinPrivateDiscord")} testId="payment-discord-cta" />
            </div>
          )}
        </>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <>
          <XCircle className="mx-auto text-red-500" size={44} />
          <h1 data-testid={PAYMENT.statusText} className="mt-6 font-display font-bold text-3xl text-slate-900">
            {status === "expired" ? t("paymentReturnUi.sessionExpired") : status === "timeout" ? t("paymentReturnUi.stillProcessing") : t("paymentReturnUi.somethingWentWrong")}
          </h1>
          <p className="mt-3 text-slate-600 max-w-md mx-auto">
            {error || t("paymentReturnUi.tryAgainFromPricing")}
          </p>
          <Button
            onClick={() => navigate("/pricing")}
            className="mt-8 bg-slate-900 hover:bg-slate-800 text-white h-11 px-8"
          >
            {t("paymentReturnUi.backToPricing")}
          </Button>
        </>
      )}
    </div>
  );
}
